import * as React from 'react';
import _ from 'lodash';
import Input from '../Input/Input';
import useUpdateRef from '../../../hooks/useUpdateRef';
import { useHover } from '../../../hooks/dom/useHover';
import { useDebounce } from '../../../hooks/code/useDebounce';
import cn from '~modules/cn';
import CloseIcon from '../../../assets/icons/v2design/basics/close.svg';
import './autocomplete-token-input.scss';

export type Token<T> = T;

type Props<T> = {
    placeholder?: string;
    tokens?: Token<T>[];
    onChange?: (values: { value: Token<T>; isValid: boolean }[]) => void;
    className?: string;
    validate?: (value: T) => boolean;
    debounceCurrentValue?: number;
};

const AutocompleteTokenInput = <T extends string | number>(props: Props<T>): JSX.Element => {
    const { validate, tokens = [] } = props;
    const [componentRef, isHovered] = useHover<HTMLDivElement>();
    const inputRef = React.useRef(null) as React.MutableRefObject<HTMLInputElement | null>;
    const [tokensValue, setTokensValue] = React.useState(
        tokens.map((value) => ({
            value,
            isValid: validate?.(value) ?? true
        }))
    );
    const [inputValue, setInputValue] = React.useState('');
    const [focused, setFocused] = React.useState(false);
    const onChangeRef = useUpdateRef(props.onChange);
    const debouncedInputValue = useDebounce<string>(inputValue, props.debounceCurrentValue ?? 500);

    React.useEffect(() => {
        onChangeRef.current?.(tokensValue);
    }, [tokensValue, onChangeRef]);

    React.useEffect(() => {
        // send current inputValue if valid
        const value = debouncedInputValue as T;
        const newValue = [...tokensValue];
        if (value) {
            newValue.push({ value: value as T, isValid: !validate || validate?.(value as T) });
        }
        onChangeRef.current?.(newValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInputValue, onChangeRef]);

    React.useEffect(() => {
        // Empty inputValue triggers on change
        if (inputValue === '' && tokensValue?.length > 0) {
            onChangeRef.current?.(tokensValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, onChangeRef]);

    return (
        <div
            ref={componentRef}
            className={cn(
                'autocomplete-token-input',
                { 'autocomplete-token-input--focused': focused, 'autocomplete-token-input--hovered': isHovered },
                props.className
            )}
            onKeyUp={(e) => {
                if (e.keyCode === 37 || e.keyCode === 39) {
                    const childNodes = componentRef?.current?.childNodes;
                    let activeIndex = 0;
                    Array.from(childNodes).forEach((el, index) => {
                        if (el === document.activeElement) {
                            activeIndex = index;
                        }
                    });
                    const nextActiveNode = childNodes[e.keyCode === 37 ? activeIndex - 1 : activeIndex + 1] as HTMLElement;
                    nextActiveNode?.focus();
                }
            }}
        >
            {tokensValue.map(({ value, isValid }, index) => (
                <div
                    key={index}
                    tabIndex={0}
                    onKeyUp={(e) => {
                        if (e.keyCode === 8 && !inputValue) {
                            inputRef?.current?.focus();
                            setTokensValue(_.remove(tokensValue, (val, valIndex) => valIndex !== index));
                        }
                    }}
                    className={cn('autocomplete-tokens', { 'autocomplete-tokens--invalid': !isValid })}
                >
                    <span className="autocomplete-tokens__value">{value}</span>
                    <span
                        className="autocomplete-tokens__handler"
                        onClick={() => {
                            setTokensValue(_.remove(tokensValue, (tokenValue, tokenIndex) => tokenIndex !== index));
                        }}
                    >
                        <CloseIcon />
                    </span>
                </div>
            ))}
            <Input
                ref={inputRef}
                onChange={(e) => {
                    setInputValue(e.target.value);
                }}
                placeholder={props.placeholder}
                value={inputValue}
                onFocus={() => {
                    setFocused(true);
                }}
                onBlur={() => {
                    setFocused(false);
                }}
                onKeyUp={(e): void => {
                    if (e.keyCode === 13 && inputValue) {
                        setTokensValue([...tokensValue, { value: inputValue as T, isValid: validate?.(inputValue as T) ?? true }]);
                        setInputValue('');
                    } else if (e.keyCode === 8 && !inputValue && tokensValue.length) {
                        const nextActiveNode = componentRef?.current?.childNodes[tokensValue.length - 1] as HTMLElement;
                        nextActiveNode?.focus();
                    } else if (e.keyCode === 37 || e.keyCode === 39) {
                        if (e.currentTarget.selectionStart !== 0) {
                            e.stopPropagation();
                        }
                    }
                }}
            />
        </div>
    );
};

export default AutocompleteTokenInput;
